import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useFieldArray, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useIdempotencyKey } from '@/api/idempotency'
import { poolKindOf, poolLabel, usePools } from '@/api/pools'
import type { Pool } from '@/api/pools'
import { useCreateRestock } from '@/api/restocks'
import type { RestockBody } from '@/api/restocks'
import { useWorkspace } from '@/api/workspace'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatCostPerUnit, formatMoney } from '@/components/materials/pool-format'
import { needsBackdateReason, restockErrorMessage } from '@/components/restocks/error-message'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

// Create a Restock (UI-SCREENS M-05, #66). Phone first: one card per line, 44px targets,
// numeric keyboards. Each line shows the cost per unit it buys at, and the screen states that
// shipping is a separate Expense (07) which never enters that cost.
export const Route = createFileRoute('/w/$slug/materials/restocks/new')({
  component: NewRestock,
  staticData: { breadcrumb: 'restocks.form.title' },
})

function NewRestock() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const workspace = useWorkspace(slug)
  const pools = usePools(slug)

  return (
    <section className="flex max-w-2xl flex-col gap-4">
      <Link to="/w/$slug/materials/restocks" params={{ slug }} className="text-sm underline-offset-4 hover:underline">
        {t('restocks.back')}
      </Link>
      <h1 className="font-heading text-xl font-semibold">{t('restocks.form.title')}</h1>

      {workspace.isPending || pools.isPending ? (
        <LoadingSkeleton variant="form" rows={4} />
      ) : workspace.isError || !workspace.data || pools.isError || !pools.data ? (
        <p role="alert" className="text-sm text-destructive">
          {t('errors.generic')}
        </p>
      ) : !workspace.data.adopted_on ? (
        <Missing message={t('restocks.errors.adoptionDateNotSet')} />
      ) : pools.data.length === 0 ? (
        <Missing message={t('restocks.form.noPools')} action={{ to: 'pools', slug }} />
      ) : (
        <RestockForm
          slug={slug}
          adoptedOn={workspace.data.adopted_on}
          timezone={workspace.data.timezone}
          pools={pools.data}
        />
      )}
    </section>
  )
}

function Missing({ message, action }: { message: string; action?: { to: 'pools'; slug: string } }) {
  const { t } = useTranslation()

  return (
    <Card>
      <CardContent className="flex flex-col items-start gap-3 py-4">
        <p role="status" className="text-sm">
          {message}
        </p>
        {action ? (
          <Button asChild variant="outline" className="h-11">
            <Link to="/w/$slug/materials" params={{ slug: action.slug }}>
              {t('restocks.form.goToPools')}
            </Link>
          </Button>
        ) : null}
      </CardContent>
    </Card>
  )
}

const EMPTY_LINE = { poolId: '', poolKind: '', qty: '', amount: '' }

// Today in the Workspace's timezone: the business date may never be after it (domain-rules).
function todayIn(timezone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(new Date())
}

function RestockForm({
  slug,
  adoptedOn,
  timezone,
  pools,
}: {
  slug: string
  adoptedOn: string
  timezone: string
  pools: Pool[]
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const create = useCreateRestock()
  const idempotency = useIdempotencyKey()
  // The backdate limit lives on the server; the reason field appears once it asks for one.
  const [askReason, setAskReason] = useState(false)
  const today = todayIn(timezone)

  // Every stored number is an integer (domain-rules): qty > 0, whole-Rp line total ≥ 0. The
  // server checks the same rules; these are only for a quicker answer.
  const wholeNumber = (message: string, min: number) =>
    z.string().refine((value) => /^\d+$/.test(value) && Number(value) >= min, { message })

  const formSchema = z.object({
    occurredOn: z.string().min(1, { message: t('restocks.form.required') }),
    vendor: z.string(),
    note: z.string(),
    backdateReason: z.string(),
    lines: z
      .array(
        z.object({
          poolId: z.string().min(1, { message: t('restocks.form.required') }),
          poolKind: z.string(),
          qty: wholeNumber(t('restocks.form.qtyInvalid'), 1),
          amount: wholeNumber(t('restocks.form.amountInvalid'), 0),
        }),
      )
      .min(1),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { occurredOn: today, vendor: '', note: '', backdateReason: '', lines: [EMPTY_LINE] },
  })
  const lines = useFieldArray({ control: form.control, name: 'lines' })
  // useWatch rather than form.watch: it returns a value the React compiler can memoize.
  const watched = useWatch({ control: form.control, name: 'lines' })

  const onSubmit = form.handleSubmit((values) => {
    const reason = values.backdateReason.trim()
    const body: RestockBody = {
      restock: {
        occurred_on: values.occurredOn,
        vendor: values.vendor.trim() === '' ? null : values.vendor.trim(),
        note: values.note.trim() === '' ? null : values.note.trim(),
        ...(reason === '' ? {} : { backdate_reason: reason }),
        lines: values.lines.map((line) => ({
          pool_id: Number(line.poolId),
          pool_kind: line.poolKind,
          qty: Number(line.qty),
          amount: Number(line.amount),
        })),
      },
    }
    create.mutate(
      { params: { path: { slug }, header: idempotency.headerFor(body) }, body },
      {
        onSuccess: (data) =>
          navigate({ to: '/w/$slug/materials/restocks/$restockId', params: { slug, restockId: String(data.id) } }),
        onSettled: (_data, error) => {
          if (needsBackdateReason(error)) setAskReason(true)
          idempotency.settle(error)
        },
      },
    )
  })

  const total = watched.reduce((sum, line) => sum + wholeAmount(line?.amount), 0)

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
      <p role="alert" aria-live="assertive" className="text-sm text-destructive empty:hidden">
        {create.isError ? restockErrorMessage(t, create.error) : ''}
      </p>

      <div className="flex flex-col gap-2">
        <Label htmlFor="occurredOn">{t('restocks.form.date')}</Label>
        <Input id="occurredOn" type="date" className="h-11" min={adoptedOn} max={today} {...form.register('occurredOn')} />
        <p className="text-sm text-muted-foreground">{t('restocks.form.dateHint')}</p>
        <FieldError message={form.formState.errors.occurredOn?.message} />
      </div>

      {askReason ? (
        <div className="flex flex-col gap-2">
          <Label htmlFor="backdateReason">{t('restocks.form.backdateReason')}</Label>
          <Input id="backdateReason" className="h-11" {...form.register('backdateReason')} />
          <p className="text-sm text-muted-foreground">{t('restocks.form.backdateReasonHint')}</p>
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        <Label htmlFor="vendor">{t('restocks.form.vendor')}</Label>
        <Input id="vendor" className="h-11" {...form.register('vendor')} />
      </div>

      <div className="flex flex-col gap-3">
        {lines.fields.map((field, index) => (
          <Card key={field.id}>
            <CardContent className="flex flex-col gap-3 py-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor={`pool-${index}`}>{t('restocks.form.pool')}</Label>
                {/* A native select on purpose: a Workspace can hold dozens of Pools, and on a
                    phone the OS picker beats a scrolling popover. */}
                <select
                  id={`pool-${index}`}
                  className="h-11 w-full rounded-lg border border-input bg-transparent px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
                  {...form.register(`lines.${index}.poolId`, {
                    onChange: (event) => form.setValue(`lines.${index}.poolKind`, poolKindOf(pools, event.target.value)),
                  })}
                >
                  <option value="">{t('restocks.form.selectPool')}</option>
                  {pools.map((pool) => (
                    <option key={pool.id} value={String(pool.id)}>
                      {poolLabel(pool)}
                    </option>
                  ))}
                </select>
                <FieldError message={form.formState.errors.lines?.[index]?.poolId?.message} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-2">
                  <Label htmlFor={`qty-${index}`}>{t('restocks.form.qty')}</Label>
                  <Input
                    id={`qty-${index}`}
                    inputMode="numeric"
                    className="h-11"
                    {...form.register(`lines.${index}.qty`)}
                  />
                  <FieldError message={form.formState.errors.lines?.[index]?.qty?.message} />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor={`amount-${index}`}>{t('restocks.form.amount')}</Label>
                  <Input
                    id={`amount-${index}`}
                    inputMode="numeric"
                    className="h-11"
                    {...form.register(`lines.${index}.amount`)}
                  />
                  <FieldError message={form.formState.errors.lines?.[index]?.amount?.message} />
                </div>
              </div>

              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-muted-foreground">
                  {t('restocks.form.costPerUnit')}: {costPreview(watched[index])}
                </span>
                {lines.fields.length > 1 ? (
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-11 text-destructive"
                    onClick={() => lines.remove(index)}
                  >
                    <Trash2 aria-hidden="true" />
                    {t('restocks.form.removeLine')}
                  </Button>
                ) : null}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Button type="button" variant="outline" className="h-11 self-start" onClick={() => lines.append(EMPTY_LINE)}>
        {t('restocks.form.addLine')}
      </Button>

      <div className="flex flex-col gap-2">
        <Label htmlFor="note">{t('restocks.form.note')}</Label>
        <Input id="note" className="h-11" {...form.register('note')} />
      </div>

      <p className="text-base font-medium">
        {t('restocks.form.total')}: Rp {formatMoney(total)}
      </p>
      <p className="text-sm text-muted-foreground">{t('restocks.form.expenseHint')}</p>
      <p className="text-sm text-muted-foreground">{t('restocks.form.shippingHint')}</p>

      <div className="flex flex-wrap gap-2">
        <Button type="submit" className="h-11" disabled={create.isPending}>
          {t(create.isPending ? 'restocks.form.saving' : 'restocks.form.save')}
        </Button>
        <Button asChild variant="ghost" className="h-11">
          <Link to="/w/$slug/materials/restocks" params={{ slug }}>
            {t('restocks.form.cancel')}
          </Link>
        </Button>
      </div>
    </form>
  )
}

function wholeAmount(value: string | undefined): number {
  const amount = Number(value)
  return Number.isInteger(amount) && amount >= 0 ? amount : 0
}

// amount ÷ qty, the cost per unit this line buys at. Display only; the Pool's own cost per unit
// is the weighted average the ledger derives (domain-rules Numbers & rounding).
function costPreview(line: { qty: string; amount: string } | undefined): string {
  const qty = Number(line?.qty)
  const amount = wholeAmount(line?.amount)
  if (!Number.isInteger(qty) || qty <= 0) return '—'
  return `Rp ${formatCostPerUnit(amount / qty)}`
}

function FieldError({ message }: { message?: string }) {
  return (
    <p role="alert" className="text-sm text-destructive empty:hidden">
      {message ?? ''}
    </p>
  )
}
